import { Extension, Mark, getHTMLFromFragment, Node as Node$1, extensions as extensions$1 } from '@tiptap/core';
import { DOMParser, Fragment } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import MarkdownIt from 'markdown-it';
import { defaultMarkdownSerializer, MarkdownSerializerState } from '@tiptap/pm/markdown';
function tuiElementFromString(value) {
  return new window.DOMParser().parseFromString(`<body>${value}</body>`, 'text/html').body;
}
function tuiExtractElement(node) {
  const parent = node.parentElement;
  const prepend = parent?.cloneNode();
  while (parent?.firstChild && parent.firstChild !== node) {
    prepend?.appendChild(parent.firstChild);
  }
  if ((prepend?.childNodes?.length ?? 0) > 0 && prepend) {
    parent?.parentElement?.insertBefore(prepend, parent);
  }
  parent?.parentElement?.insertBefore(node, parent);
  if (parent?.childNodes.length === 0) {
    parent.remove();
  }
}
function tuiUnwrapElement(node) {
  const parent = node.parentNode;
  while (node?.firstChild) {
    parent?.insertBefore(node.firstChild, node);
  }
  parent?.removeChild(node);
}
function tuiEscapeHTML(value) {
  return value?.replace(/</g, '&lt;').replaceAll('>', '&gt;') ?? '';
}
const TuiMarkdownClipboard = Extension.create({
  name: 'markdownClipboard',
  addOptions() {
    return {
      transformPastedText: false,
      transformCopiedText: false
    };
  },
  addProseMirrorPlugins() {
    return [new Plugin({
      key: new PluginKey('markdownClipboard'),
      props: {
        clipboardTextParser: (text, context, plainText) => {
          if (plainText || !this.options.transformPastedText) {
            return null; // pasting with shift key prevents formatting
          }
          const parsed = this.editor.storage.markdown.parser.parse(text, {
            inline: true
          });
          return DOMParser.fromSchema(this.editor.schema).parseSlice(tuiElementFromString(parsed), {
            preserveWhitespace: true,
            context
          });
        },
        clipboardTextSerializer: slice => {
          if (!this.options.transformCopiedText) {
            return null;
          }
          return this.editor.storage.markdown.serializer.serialize(slice.content);
        }
      }
    })];
  }
});
var Bold = Mark.create({
  name: 'bold'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.marks.strong,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Code = Mark.create({
  name: 'code'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.marks.code,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
function getMarkTags(mark) {
  const schema = mark.type.schema;
  const node = schema.text(' ', [mark]);
  const html = getHTMLFromFragment(Fragment.from(node), schema);
  const match = /^(<.*?>) (<\/.*?>)$/.exec(html);
  return match ? [match[1] ?? '', match[2] ?? ''] : null;
}
var HTMLMark = Mark.create({
  name: 'markdownHTMLMark',
  addStorage() {
    return {
      markdown: {
        serialize: {
          open(_, mark) {
            if (!this.editor?.storage.markdown.options.html) {
              console.warn(`Tiptap Markdown: "${mark.type.name}" mark is only available in html mode`);
              return '';
            }
            return getMarkTags(mark)?.[0] ?? '';
          },
          close(_, mark) {
            if (!this.editor?.storage.markdown.options.html) {
              return '';
            }
            return getMarkTags(mark)?.[1] ?? '';
          }
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Italic = Mark.create({
  name: 'italic'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.marks.em,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Link = Mark.create({
  name: 'link'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.marks.link,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Strike = Mark.create({
  name: 'strike'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '~~',
          close: '~~',
          expelEnclosingWhitespace: true
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Blockquote = Node$1.create({
  name: 'blockquote'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.nodes.blockquote,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var BulletList = Node$1.create({
  name: 'bulletList'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          return state.renderList(node, '  ', () => `${this?.editor?.storage.markdown.options.bulletListMarker || '-'} `);
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var CodeBlock = Node$1.create({
  name: 'codeBlock'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          state.write(`\`\`\`${node.attrs.language || ''}\n`);
          state.text(node.textContent, false);
          state.ensureNewLine();
          state.write('```');
          state.closeBlock(node);
        },
        parse: {
          setup(markdown) {
            markdown.set({
              langPrefix: this.options?.languageClassPrefix ?? 'language-'
            });
          },
          updateDOM(element) {
            element.innerHTML = element.innerHTML.replaceAll('\n</code></pre>', '</code></pre>');
          }
        }
      }
    };
  }
});
var HTMLNode = Node$1.create({
  name: 'markdownHTMLNode',
  addStorage() {
    return {
      markdown: {
        serialize(state, node, parent) {
          if (this.editor.storage.markdown.options.html) {
            state.write(serializeHTML(node, parent));
          } else {
            console.warn(`Tiptap Markdown: "${node.type.name}" node is only available in html mode`);
            state.write(`[${node.type.name}]`);
          }
          if (node.isBlock) {
            state.closeBlock(node);
          }
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
function serializeHTML(node, parent) {
  const schema = node.type.schema;
  const html = getHTMLFromFragment(Fragment.from(node), schema);
  if (node.isBlock && (parent instanceof Fragment || parent.type.name === schema.topNodeType.name)) {
    return formatBlock(html);
  }
  return html;
}
function formatBlock(html) {
  const dom = tuiElementFromString(html);
  const element = dom.firstElementChild;
  if (element) {
    element.innerHTML = element.innerHTML.trim() ? `\n${element.innerHTML}\n` : '\n';
  }
  return element?.outerHTML ?? '';
}
var HardBreak = Node$1.create({
  name: 'hardBreak'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node, parent, index) {
          for (let i = index + 1; i < parent.childCount; i++) {
            if (parent.child(i).type !== node.type) {
              state.write(state.inTable ? HTMLNode.storage.markdown.serialize.call(this, state, node, parent) : '\\\n');
              return;
            }
          }
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Heading = Node$1.create({
  name: 'heading'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.nodes.heading,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var HorizontalRule = Node$1.create({
  name: 'horizontalRule'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.nodes.horizontal_rule,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Image = Node$1.create({
  name: 'image'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.nodes.image,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var ListItem = Node$1.create({
  name: 'listItem'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.nodes.list_item,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
function findIndexOfAdjacentNode(node, parent, index) {
  let i = 0;
  for (; index - i > 0; i++) {
    if (parent.child(index - i - 1).type.name !== node.type.name) {
      break;
    }
  }
  return i;
}
var OrderedList = Node$1.create({
  name: 'orderedList'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node, parent, index) {
          const start = node.attrs.start || 1;
          const maxW = String(start + node.childCount - 1).length;
          const space = state.repeat(' ', maxW + 2);
          const adjacentIndex = findIndexOfAdjacentNode(node, parent, index);
          const separator = adjacentIndex % 2 ? ') ' : '. ';
          state.renderList(node, space, i => {
            const nStr = String(start + i);
            return state.repeat(' ', maxW - nStr.length) + nStr + separator;
          });
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
var Paragraph = Node$1.create({
  name: 'paragraph'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: defaultMarkdownSerializer.nodes.paragraph,
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
function tuiChildNodes(node) {
  return node?.content?.content ?? [];
}
var Table = Node$1.create({
  name: 'table'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node, parent) {
          if (!isMarkdownSerializable(node)) {
            HTMLNode.storage.markdown.serialize.call(this, state, node, parent);
            return;
          }
          state.inTable = true;
          node.forEach((row, _p, i) => {
            state.write('| ');
            row.forEach((col, __p, j) => {
              if (j) {
                state.write(' | ');
              }
              const cellContent = col.firstChild;
              if (cellContent?.textContent.trim()) {
                state.renderInline(cellContent);
              }
            });
            state.write(' |');
            state.ensureNewLine();
            if (!i) {
              const delimiterRow = Array.from({
                length: row.childCount
              }).map(() => '---').join(' | ');
              state.write(`| ${delimiterRow} |`);
              state.ensureNewLine();
            }
          });
          state.closeBlock(node);
          state.inTable = false;
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
function hasSpan(node) {
  return node.attrs.colspan > 1 || node.attrs.rowspan > 1;
}
function isMarkdownSerializable(node) {
  const rows = tuiChildNodes(node);
  const firstRow = rows[0];
  const bodyRows = rows.slice(1);
  if (tuiChildNodes(firstRow).some(cell => cell.type.name !== 'tableHeader' || hasSpan(cell) || cell.childCount > 1)) {
    return false;
  }
  return !bodyRows.some(row => tuiChildNodes(row).some(cell => cell.type.name === 'tableHeader' || hasSpan(cell) || cell.childCount > 1));
}
var TaskItem = Node$1.create({
  name: 'taskItem'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          const check = node.attrs.checked ? '[x]' : '[ ]';
          state.write(`${check} `);
          state.renderContent(node);
        },
        parse: {
          updateDOM(element) {
            Array.from(element.querySelectorAll('.task-list-item')).forEach(item => {
              const input = item.querySelector('input');
              item.setAttribute('data-type', 'taskItem');
              if (input) {
                item.setAttribute('data-checked', input.checked.toString());
                input.remove();
              }
            });
          }
        }
      }
    };
  }
});
let disableCheckboxes = true;
let useLabelWrapper = false;
let useLabelAfter = false;
function tuiMarkdownItTaskList(md, options) {
  if (options) {
    disableCheckboxes = !options.enabled;
    useLabelWrapper = !!options.label;
    useLabelAfter = !!options.labelAfter;
  }
  md.core.ruler.after('inline', 'github-task-lists', state => {
    const tokens = state.tokens;
    for (let i = 2; i < tokens.length; i++) {
      if (isTodoItem(tokens, i)) {
        todoify(tokens[i], state.Token);
        attrSet(tokens[i - 2], 'class', `task-list-item${!disableCheckboxes ? ' enabled' : ''}`);
        attrSet(tokens[parentToken(tokens, i - 2)], 'class', 'contains-task-list');
      }
    }
  });
}
function attrSet(token, name, value) {
  const index = token.attrIndex(name);
  const attr = [name, value];
  if (index < 0) {
    token.attrPush(attr);
  } else if (token.attrs) {
    token.attrs[index] = attr;
  }
}
function parentToken(tokens, index) {
  const targetLevel = (tokens[index]?.level ?? 0) - 1;
  for (let i = index - 1; i >= 0; i--) {
    if (tokens[i]?.level === targetLevel) {
      return i;
    }
  }
  return -1;
}
function isTodoItem(tokens, index) {
  return (tokens[index] && isInline(tokens[index]) && tokens[index - 1] && isParagraph(tokens[index - 1]) && tokens[index - 2] && isListItem(tokens[index - 2]) && startsWithTodoMarkdown(tokens[index])) ?? false;
}
function todoify(token, TokenConstructor) {
  token.children?.unshift(makeCheckbox(token, TokenConstructor));
  if (token.children?.[1]) {
    token.children[1].content = token.children[1].content.slice(3);
  }
  if (token.content) {
    token.content = token.content.slice(3);
  }
  if (useLabelWrapper) {
    if (useLabelAfter) {
      token.children?.pop();
      // Use large random number as id property of the checkbox.
      const id = `task-item-${Math.ceil(Math.random() * (10000 * 1000) - 1000)}`;
      if (token.children?.[0]) {
        token.children[0].content = `${token.children[0].content.slice(0, -1)} id="${id}">`;
      }
      token.children?.push(afterLabel(token.content, id, TokenConstructor));
    } else {
      token.children?.unshift(beginLabel(TokenConstructor));
      token.children?.push(endLabel(TokenConstructor));
    }
  }
}
function makeCheckbox(token, TokenConstructor) {
  const checkbox = new TokenConstructor('html_inline', '', 0);
  const disabledAttr = disableCheckboxes ? ' disabled="" ' : '';
  if (token?.content.startsWith('[ ] ')) {
    checkbox.content = `<input class="task-list-item-checkbox"${disabledAttr}type="checkbox">`;
  } else if (token?.content.startsWith('[x] ') || token?.content.startsWith('[X] ')) {
    checkbox.content = `<input class="task-list-item-checkbox" checked=""${disabledAttr}type="checkbox">`;
  }
  return checkbox;
}
// these next two functions are kind of hacky; probably should really be a
// true block-level token with .tag=='label'
function beginLabel(TokenConstructor) {
  const token = new TokenConstructor('html_inline', '', 0);
  token.content = '<label>';
  return token;
}
function endLabel(TokenConstructor) {
  const token = new TokenConstructor('html_inline', '', 0);
  token.content = '</label>';
  return token;
}
function afterLabel(content, id, TokenConstructor) {
  const token = new TokenConstructor('html_inline', '', 0);
  token.content = `<label class="task-list-item-label" for="${id}">${content}</label>`;
  token.attrs = [{
    for: id
  }];
  return token;
}
function isInline(token) {
  return token.type === 'inline';
}
function isParagraph(token) {
  return token.type === 'paragraph_open';
}
function isListItem(token) {
  return token.type === 'list_item_open';
}
function startsWithTodoMarkdown(token) {
  // leading whitespace in a list item is already trimmed off by markdown-it
  return token.content.startsWith('[ ] ') || token.content.startsWith('[x] ') || token.content.startsWith('[X] ');
}
var TaskList = Node$1.create({
  name: 'taskList'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize: BulletList.storage.markdown.serialize,
        parse: {
          setup(markdown) {
            markdown.use(tuiMarkdownItTaskList);
          },
          updateDOM(element) {
            Array.from(element.querySelectorAll('.contains-task-list')).forEach(list => {
              list.setAttribute('data-type', 'taskList');
            });
          }
        }
      }
    };
  }
});
var Text = Node$1.create({
  name: 'text'
}).extend({
  addStorage() {
    return {
      markdown: {
        serialize(state, node) {
          state.text(tuiEscapeHTML(node.text));
        },
        parse: {
          // handled by markdown-it
        }
      }
    };
  }
});
const extensions = [Blockquote, BulletList, CodeBlock, HardBreak, Heading, HorizontalRule, HTMLNode, Image, ListItem, OrderedList, Paragraph, Table, TaskItem, TaskList, Text, Bold, Code, HTMLMark, Italic, Link, Strike];
function tuiGetMarkdownSpec(extension) {
  const markdownSpec = extension.storage?.markdown;
  const defaultMarkdownSpec = extensions.find(e => e.name === extension.name)?.storage.markdown;
  if (markdownSpec || defaultMarkdownSpec) {
    return {
      ...defaultMarkdownSpec,
      ...markdownSpec
    };
  }
  return null;
}
class TuiEditorMarkdownParser {
  constructor(editor, {
    html,
    linkify,
    breaks
  }) {
    this.editor = editor;
    this.md = this.withPatchedRenderer(MarkdownIt({
      html,
      linkify,
      breaks
    }));
  }
  parse(content, {
    inline
  } = {}) {
    if (typeof content === 'string') {
      this.editor.extensionManager.extensions.forEach(extension => tuiGetMarkdownSpec(extension)?.parse?.setup?.call({
        editor: this.editor,
        options: extension.options
      }, this.md));
      const renderedHTML = this.md.render(content);
      const element = tuiElementFromString(renderedHTML);
      this.editor.extensionManager.extensions.forEach(extension => tuiGetMarkdownSpec(extension)?.parse?.updateDOM?.call({
        editor: this.editor,
        options: extension.options
      }, element));
      this.normalizeDOM(element, {
        inline,
        content
      });
      return element.innerHTML;
    }
    return content;
  }
  normalizeDOM(node, {
    inline,
    content
  }) {
    this.normalizeBlocks(node);
    // remove all \n appended by markdown-it
    node.querySelectorAll('*').forEach(el => {
      if (el.nextSibling?.nodeType === Node.TEXT_NODE && !el.closest('pre')) {
        el.nextSibling.textContent = el.nextSibling.textContent?.replace(/^\n/, '') ?? '';
      }
    });
    if (inline) {
      this.normalizeInline(node, content);
    }
    return node;
  }
  normalizeBlocks(node) {
    const blocks = Object.values(this.editor.schema.nodes).filter(node => node.isBlock);
    const selector = blocks.map(block => block.spec.parseDOM?.map(spec => spec.tag)).flat().filter(Boolean).join(',');
    if (!selector) {
      return;
    }
    Array.from(node.querySelectorAll(selector)).forEach(el => {
      if (el.parentElement?.matches('p')) {
        tuiExtractElement(el);
      }
    });
  }
  normalizeInline(node, content) {
    if (node.firstElementChild?.matches('p')) {
      const firstParagraph = node.firstElementChild;
      const {
        nextElementSibling,
        innerHTML
      } = firstParagraph;
      const startSpaces = /^\s+/.exec(content)?.[0] ?? '';
      const endSpaces = !nextElementSibling ? /\s+$/.exec(content)?.[0] ?? '' : '';
      if (/^\n\n/.exec(content)) {
        firstParagraph.innerHTML = `${innerHTML}${endSpaces}`;
        return;
      }
      tuiUnwrapElement(firstParagraph);
      node.innerHTML = `${startSpaces}${node.innerHTML}${endSpaces}`;
    }
  }
  withPatchedRenderer(md) {
    const withoutNewLine = renderer => (...args) => {
      // @ts-ignore
      const rendered = renderer?.(...args);
      if (rendered === '\n') {
        return rendered;
      }
      if (rendered?.endsWith('\n')) {
        return rendered.slice(0, -1);
      }
      return rendered ?? '';
    };
    md.renderer.rules.hardbreak = withoutNewLine(md.renderer.rules.hardbreak);
    md.renderer.rules.softbreak = withoutNewLine(md.renderer.rules.softbreak);
    md.renderer.rules.fence = withoutNewLine(md.renderer.rules.fence);
    md.renderer.rules.code_block = withoutNewLine(md.renderer.rules.code_block);
    md.renderer.renderToken = withoutNewLine(md.renderer.renderToken.bind(md.renderer));
    return md;
  }
}
const md = MarkdownIt();
function scanDelims(text, pos) {
  // @ts-ignore
  md.inline.State.prototype.scanDelims.call({
    src: text,
    posMax: text.length
  });
  // @ts-ignore
  const state = new md.inline.State(text, null, null, []);
  return state.scanDelims(pos, true);
}
function trimStart(options) {
  const {
    text,
    delim,
    from,
    to
  } = options;
  let pos = from;
  let res = text;
  while (pos < to) {
    if (scanDelims(res, pos).can_open) {
      break;
    }
    res = tuiShiftDelim({
      text: res,
      delim,
      start: pos,
      offset: 1
    });
    pos++;
  }
  return {
    text: res,
    from: pos,
    to
  };
}
function trimEnd(options) {
  const {
    text,
    delim,
    from,
    to
  } = options;
  let pos = to;
  let res = text;
  while (pos > from) {
    if (scanDelims(res, pos).can_close) {
      break;
    }
    res = tuiShiftDelim({
      text: res,
      delim,
      start: pos,
      offset: -1
    });
    pos--;
  }
  return {
    text: res,
    from,
    to: pos
  };
}
function tuiTrimInline(options) {
  const {
    text,
    delim,
    from,
    to
  } = options;
  let state = {
    text,
    from,
    to
  };
  state = trimStart({
    text: state.text,
    delim,
    from: state.from,
    to: state.to
  });
  state = trimEnd({
    text: state.text,
    delim,
    from: state.from,
    to: state.to
  });
  if (state.to - state.from < delim.length + 1) {
    state.text = state.text.slice(0, Math.max(0, state.from)) + state.text.slice(Math.max(0, state.to + delim.length));
  }
  return state.text;
}
function tuiShiftDelim(options) {
  const {
    text,
    delim,
    start,
    offset
  } = options;
  let res = text.slice(0, Math.max(0, start)) + text.slice(Math.max(0, start + delim.length));
  res = res.slice(0, Math.max(0, start + offset)) + delim + res.slice(Math.max(0, start + offset));
  return res;
}
class TuiMarkdownSerializerState extends MarkdownSerializerState {
  constructor(nodes, marks, options) {
    // @ts-ignore
    super(nodes, marks, options ?? {});
    this.inTable = false;
    this.inlines = [];
  }
  render(node, parent, index) {
    super.render(node, parent, index);
    const top = this.inlines[this.inlines.length - 1];
    if (top?.start && top?.end) {
      const {
        delimiter,
        start,
        end
      } = this.normalizeInline(top);
      this.out = tuiTrimInline({
        text: this.out,
        delim: delimiter,
        from: start,
        to: end
      });
      this.inlines.pop();
    }
  }
  markString(mark, open, parent, index) {
    const info = this.marks[mark.type.name];
    if (info.expelEnclosingWhitespace) {
      if (open) {
        this.inlines.push({
          start: this.out.length,
          delimiter: info.open
        });
      } else {
        const top = this.inlines.pop();
        this.inlines.push({
          ...top,
          end: this.out.length
        });
      }
    }
    return super.markString(mark, open, parent, index);
  }
  normalizeInline(inline) {
    let {
      start
    } = inline;
    while (this.out.charAt(start).match(/\s/)) {
      start++;
    }
    return {
      ...inline,
      start
    };
  }
}
class TuiMarkdownSerializer {
  constructor(editor) {
    this.editor = editor;
  }
  get nodes() {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      ...Object.fromEntries(Object.keys(this.editor.schema.nodes).map(name => [name, this.serializeNode(HTMLNode)])),
      ...Object.fromEntries(this.editor.extensionManager.extensions.filter(extension => extension.type === 'node' && this.serializeNode(extension)).map(extension => [extension.name, this.serializeNode(extension)]) ?? [])
    };
  }
  get marks() {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      ...Object.fromEntries(Object.keys(this.editor.schema.marks).map(name => [name, this.serializeMark(HTMLMark)])),
      ...Object.fromEntries(this.editor.extensionManager.extensions.filter(extension => extension.type === 'mark' && this.serializeMark(extension)).map(extension => [extension.name, this.serializeMark(extension)]) ?? [])
    };
  }
  serialize(content) {
    const state = new TuiMarkdownSerializerState(this.nodes, this.marks, {
      hardBreakNodeName: HardBreak.name
    });
    state.renderContent(content);
    return state.out;
  }
  serializeNode(node) {
    return tuiGetMarkdownSpec(node)?.serialize?.bind({
      editor: this.editor,
      options: node.options
    });
  }
  serializeMark(mark) {
    const serialize = tuiGetMarkdownSpec(mark)?.serialize;
    return serialize ? {
      ...serialize,
      open: typeof serialize.open === 'function' ? serialize.open.bind({
        editor: this.editor,
        options: mark.options
      }) : serialize.open,
      close: typeof serialize.close === 'function' ? serialize.close.bind({
        editor: this.editor,
        options: mark.options
      }) : serialize.close
    } : null;
  }
}
const TuiMarkdownTightLists = Extension.create({
  name: 'markdownTightLists',
  addOptions: () => ({
    tight: true,
    tightClass: 'tight',
    listTypes: ['bulletList', 'orderedList']
  }),
  addGlobalAttributes() {
    return [{
      types: this.options.listTypes,
      attributes: {
        tight: {
          default: this.options.tight,
          parseHTML: element => element.getAttribute('data-tight') === 'true' || !element.querySelector('p'),
          renderHTML: attributes => ({
            class: attributes.tight ? this.options.tightClass : null,
            'data-tight': attributes.tight ? 'true' : null
          })
        }
      }
    }];
  },
  addCommands() {
    return {
      toggleTight: (tight = null) => ({
        editor,
        commands
      }) => this.options.listTypes.some(name => {
        if (!editor.isActive(name)) {
          return false;
        }
        const attrs = editor.getAttributes(name);
        return commands.updateAttributes(name, {
          tight: tight ?? !attrs?.tight
        });
      })
    };
  }
});
const TuiMarkdown = Extension.create({
  name: 'markdown',
  priority: 50,
  addOptions() {
    return {
      html: true,
      tightLists: true,
      tightListClass: 'tight',
      bulletListMarker: '-',
      linkify: false,
      breaks: false,
      transformPastedText: false,
      transformCopiedText: false
    };
  },
  addCommands() {
    const commands = extensions$1?.Commands?.config?.addCommands?.();
    return {
      setContent: (content, emitUpdate, parseOptions) => props => commands?.setContent?.(props.editor.storage.markdown.parser.parse(content), emitUpdate, parseOptions)(props),
      insertContentAt: (range, content, options) => props => commands?.insertContentAt?.(range, props.editor.storage.markdown.parser.parse(content, {
        inline: true
      }), options)(props)
    };
  },
  onBeforeCreate() {
    this.editor.storage.markdown = {
      options: {
        ...this.options
      },
      parser: new TuiEditorMarkdownParser(this.editor, this.options),
      serializer: new TuiMarkdownSerializer(this.editor),
      getMarkdown: () => this.editor.storage.markdown.serializer.serialize(this.editor.state.doc)
    };
    this.editor.options.initialContent = this.editor.options.content;
    this.editor.options.content = this.editor.storage.markdown.parser.parse(this.editor.options.content);
  },
  onCreate() {
    this.editor.options.content = this.editor.options.initialContent;
    delete this.editor.options.initialContent;
  },
  addStorage() {
    return {
      /// storage will be defined in onBeforeCreate() to prevent initial object overriding
    };
  },
  addExtensions() {
    return [TuiMarkdownTightLists.configure({
      tight: this.options.tightLists,
      tightClass: this.options.tightListClass
    }), TuiMarkdownClipboard.configure({
      transformPastedText: this.options.transformPastedText,
      transformCopiedText: this.options.transformCopiedText
    })];
  }
});

/**
 * Generated bundle index. Do not edit.
 */

export { TuiEditorMarkdownParser, TuiMarkdown, TuiMarkdownClipboard, TuiMarkdownSerializer, TuiMarkdownSerializerState, TuiMarkdownTightLists, tuiChildNodes, tuiElementFromString, tuiEscapeHTML, tuiExtractElement, tuiGetMarkdownSpec, tuiMarkdownItTaskList, tuiShiftDelim, tuiTrimInline, tuiUnwrapElement };
